/**
 * Questions store (R1 Q2).
 *
 * The AI's durable questions (technical design §10.4) per document, shared by
 * the AI panel and the document overview, plus what happened to each answer
 * given on this page. Fed by `GET /bids/{id}/pack-asks?kind=question` and by
 * the agent store's `question_raised` events; kept out of `agent-store.ts` so
 * that store does not grow.
 *
 * `answer` / `defer` go through the question PATCH (`action: 'answer' | 'defer'`),
 * used where there is no chat session (the document overview). The AI panel
 * answers through a chat turn instead (`question-actions.ts`), so the model
 * reads the answer.
 *  - "Save answer" → `answer_only`, never a drafting job;
 *  - "Save and update the draft" → `update_draft`, which may start a
 *    continuation job (the tray is told to look), or wait for the drafting
 *    already running on the document (`busy`). Offered only while the
 *    question list says so (`canUpdateDraft`: the whole-document job is
 *    switched on, R1 J8′).
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  answerQuestion,
  ApiRequestError,
  deferQuestion,
  listDocumentQuestions,
  type PackAsk,
  type QuestionAnswerEffect,
  type QuestionAnswerOutcome,
  type QuestionAsk,
  type QuestionContinuation,
} from '@/lib/api'
import { isQuestionAsk, sortQuestions } from '@/lib/question-view'
import {
  QUESTION_NOT_RECORDED_ALREADY_ANSWERED,
  QUESTION_NOT_RECORDED_SUPERSEDED,
  QUESTION_SAVE_FAILED,
} from '@/lib/question-copy'
import { useActiveJobsStore } from '@/stores/active-jobs-store'

// ===================
// Types
// ===================

export interface DocumentQuestions {
  status: 'idle' | 'loading' | 'ready' | 'error'
  /** Waiting questions first. Replaced questions are never listed. */
  asks: QuestionAsk[]
  /**
   * Whether "Save and update the draft" is offered: the question list's
   * server-computed flag (the whole-document job is switched on, R1 J8′).
   * False until the list says otherwise.
   */
  canUpdateDraft: boolean
}

/** The last thing done to a question on this page. */
export type QuestionActionState =
  | { kind: 'saving'; action: QuestionAnswerEffect | 'defer' }
  | { kind: 'answered'; continuation: QuestionContinuation | null }
  | { kind: 'deferred' }
  | { kind: 'error'; message: string }

/** A request to bring a document overview's question list into view, its first waiting question open. */
export interface QuestionsFocusRequest {
  artifactId: string
  /** New on every request, so asking twice for the same document still lands. */
  seq: number
}

interface QuestionsState {
  /** Keyed by artifact id. */
  documents: Record<string, DocumentQuestions>
  /** Keyed by question (ask) id. */
  actions: Record<string, QuestionActionState>
  /** The latest "Answer questions" request; the overview's list acts on it. */
  focusRequest: QuestionsFocusRequest | null

  load: (bidId: string, artifactId: string) => Promise<void>
  /** Questions a panel turn just raised (`question_raised`). */
  addRaised: (asks: ReadonlyArray<PackAsk>) => void
  answer: (bidId: string, askId: string, answer: string, effect: QuestionAnswerEffect) => Promise<void>
  /** "Not known yet". */
  defer: (bidId: string, askId: string) => Promise<void>
  /** For the panel's chat answer path (`question-actions.ts`), which answers through a turn. */
  setAction: (askId: string, action: QuestionActionState) => void
  /** A panel answer turn's `question_answered` outcome (via the agent store). */
  recordTurnOutcome: (askId: string, outcome: QuestionAnswerOutcome) => void
  /** Bring this document's question list into view and open its first waiting question. */
  requestFocus: (artifactId: string) => void
}

const EMPTY_DOCUMENT: DocumentQuestions = { status: 'idle', asks: [], canUpdateDraft: false }

export function selectDocumentQuestions(state: Pick<QuestionsState, 'documents'>, artifactId: string): DocumentQuestions {
  return state.documents[artifactId] ?? EMPTY_DOCUMENT
}

// ===================
// Helpers
// ===================

/** Statuses whose message the server words for the user (Q1 placeholders). */
const EXPLAINED_STATUSES: ReadonlySet<number> = new Set([403, 409, 503])

function failureMessage(error: unknown): string {
  if (
    error instanceof ApiRequestError &&
    error.statusCode !== undefined &&
    EXPLAINED_STATUSES.has(error.statusCode) &&
    error.message.trim().length > 0
  ) {
    return error.message
  }
  return QUESTION_SAVE_FAILED
}

/** Insert or replace each question in its document's list. */
function upsertAsks(
  documents: Record<string, DocumentQuestions>,
  asks: ReadonlyArray<PackAsk>
): Record<string, DocumentQuestions> {
  let next = documents
  for (const ask of asks) {
    if (!isQuestionAsk(ask) || !ask.artifactId) continue
    const current = next[ask.artifactId] ?? { status: 'ready' as const, asks: [], canUpdateDraft: false }
    const exists = current.asks.some((existing) => existing.id === ask.id)
    const merged = exists
      ? current.asks.map((existing) => (existing.id === ask.id ? ask : existing))
      : [...current.asks, ask]
    next = { ...next, [ask.artifactId]: { ...current, asks: sortQuestions(merged) } }
  }
  return next
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The questions in a `question_raised` event, read defensively: an entry
 * without an id, the question kind or its details is dropped.
 */
export function parseRaisedQuestions(data: Record<string, unknown>): PackAsk[] {
  const raw = data['questions']
  if (!Array.isArray(raw)) return []
  return raw.filter((item): item is PackAsk => {
    if (!isRecord(item) || typeof item['id'] !== 'string' || item['kind'] !== 'question') return false
    const details = item['question']
    return (
      isRecord(details) &&
      typeof details['status'] === 'string' &&
      Array.isArray(details['options']) &&
      Array.isArray(details['targets'])
    )
  })
}

const CONTINUATION_STATES = new Set(['started', 'already_started', 'busy', 'no_targets', 'unavailable'])

/**
 * A `question_answered` event's data, read defensively: an unknown outcome
 * kind, or a continuation in an unknown state, is no event at all.
 */
export function parseQuestionAnswered(
  data: Record<string, unknown>
): { askId: string; outcome: QuestionAnswerOutcome } | null {
  const askId = data['askId']
  const outcome = data['outcome']
  if (typeof askId !== 'string' || !isRecord(outcome)) return null
  switch (outcome['kind']) {
    case 'answered': {
      const continuation = outcome['continuation']
      if (continuation !== null && !(isRecord(continuation) && CONTINUATION_STATES.has(String(continuation['state'])))) {
        return null
      }
      return { askId, outcome: outcome as QuestionAnswerOutcome }
    }
    case 'deferred':
      return { askId, outcome: outcome as QuestionAnswerOutcome }
    case 'not_recorded':
      return outcome['code'] === 'question_superseded' || outcome['code'] === 'question_already_answered'
        ? { askId, outcome: outcome as QuestionAnswerOutcome }
        : null
    default:
      return null
  }
}

// ===================
// Store
// ===================

export const useQuestionsStore = create<QuestionsState>()(
  devtools(
    (set, get) => {
      const isSaving = (askId: string): boolean => get().actions[askId]?.kind === 'saving'
      const setAction = (askId: string, action: QuestionActionState): void =>
        set((state) => ({ actions: { ...state.actions, [askId]: action } }))

      return {
        documents: {},
        actions: {},
        focusRequest: null,

        requestFocus: (artifactId) => {
          set((state) => ({ focusRequest: { artifactId, seq: (state.focusRequest?.seq ?? 0) + 1 } }))
        },

        load: async (bidId, artifactId) => {
          set((state) => ({
            documents: {
              ...state.documents,
              [artifactId]: { ...selectDocumentQuestions(state, artifactId), status: 'loading' },
            },
          }))
          try {
            const { asks, canUpdateDraft } = await listDocumentQuestions(bidId, artifactId)
            const questions = sortQuestions(asks.filter(isQuestionAsk))
            set((state) => ({
              documents: {
                ...state.documents,
                [artifactId]: { status: 'ready', asks: questions, canUpdateDraft: canUpdateDraft === true },
              },
            }))
          } catch {
            set((state) => ({
              documents: {
                ...state.documents,
                [artifactId]: { ...selectDocumentQuestions(state, artifactId), status: 'error' },
              },
            }))
          }
        },

        addRaised: (asks) => {
          set((state) => ({ documents: upsertAsks(state.documents, asks) }))
        },

        answer: async (bidId, askId, answer, effect) => {
          const trimmed = answer.trim()
          if (!trimmed || isSaving(askId)) return
          setAction(askId, { kind: 'saving', action: effect })
          try {
            const result = await answerQuestion(bidId, askId, trimmed, effect)
            const continuation = result.continuation ?? null
            set((state) => ({ documents: upsertAsks(state.documents, [result.ask]) }))
            setAction(askId, { kind: 'answered', continuation })
            if (continuation?.state === 'started') {
              // Show the new drafting job in the tray without waiting for its next poll.
              useActiveJobsStore.getState().notifyJobDispatched()
            }
          } catch (error) {
            setAction(askId, { kind: 'error', message: failureMessage(error) })
          }
        },

        setAction,

        recordTurnOutcome: (askId, outcome) => {
          switch (outcome.kind) {
            case 'answered':
              setAction(askId, { kind: 'answered', continuation: outcome.continuation })
              if (outcome.continuation?.state === 'started') {
                useActiveJobsStore.getState().notifyJobDispatched()
              }
              return
            case 'deferred':
              setAction(askId, { kind: 'deferred' })
              return
            case 'not_recorded':
              setAction(askId, {
                kind: 'error',
                message: outcome.code === 'question_superseded'
                  ? QUESTION_NOT_RECORDED_SUPERSEDED
                  : QUESTION_NOT_RECORDED_ALREADY_ANSWERED,
              })
              return
          }
        },

        defer: async (bidId, askId) => {
          if (isSaving(askId)) return
          setAction(askId, { kind: 'saving', action: 'defer' })
          try {
            const result = await deferQuestion(bidId, askId)
            set((state) => ({ documents: upsertAsks(state.documents, [result.ask]) }))
            setAction(askId, { kind: 'deferred' })
          } catch (error) {
            setAction(askId, { kind: 'error', message: failureMessage(error) })
          }
        },
      }
    },
    { name: 'questions-store' }
  )
)
